import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as moment from "moment";
import * as momentTZ from "moment-timezone";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Subject } from "rxjs";
import { tap } from "rxjs/operators";
import { AuthService } from "../../app/auth/auth.service";
import { commonVar } from "../common-var/common";
import { ConfigurationService } from "../configuration/configuration.service";
import { ResourceManagementService } from "../resource-management/resource-management.service";
import { UtilityService } from "../utility-service/utility-service";

@Injectable()
export class AdmissionHisProvider {
  private baseUrl: string;
  public patientId: string;
  public tenantId: string;
  public admissionReportDetail;
  public activeReportId: string;
  public lookupValues: any;
  public validSignature: string = "";
  public admissionReportList;
  public admissionReportDetailChange = new Subject();
  public isBillingFacility: boolean = false;

  public tabList = [
    {
      name: "administrationInfo",
      icon: commonVar.ADD_ICON,
      title: "A - Administrative Information",
      show: true,
    },
    {
      name: "preference",
      icon: commonVar.ADD_ICON,
      title: "F - Preferences",
      show: true,
    },
    {
      name: "activeDiagnosis",
      icon: commonVar.ADD_ICON,
      title: "I - Active Diagnosis",
      show: true,
    },
    {
      name: "healthCondition",
      icon: commonVar.ADD_ICON,
      title: "J - Health Conditions",
      show: true,
    },
    {
      name: "medication",
      icon: commonVar.ADD_ICON,
      title: "N - Medications",
      show: true,
    },
    {
      name: "admissionRecordAdministration",
      icon: commonVar.ADD_ICON,
      title: "Z - Record Administration",
      show: true,
    },
  ];

  newTabList = [
    {
      name: "administrationInfo",
      icon: commonVar.ADD_ICON,
      title: "A - Administrative Information",
      show: true,
    },
    {
      name: "preference",
      icon: commonVar.ADD_ICON,
      title: "F - Preferences",
      show: true,
    },
    {
      name: "activeDiagnosis",
      icon: commonVar.ADD_ICON,
      title: "I - Active Diagnosis",
      show: true,
    },
    {
      name: "healthCondition",
      icon: commonVar.ADD_ICON,
      title: "J - Health Conditions",
      show: true,
    },
    {
      name: "medication",
      icon: commonVar.ADD_ICON,
      title: "N - Medications",
      show: true,
    },
    {
      name: "admissionRecordAdministration",
      icon: commonVar.ADD_ICON,
      title: "Z - Record Administration",
      show: true,
    },
  ];

  public patientStatus: string;
  orgTimezone: string;

  constructor(
    public http: HttpClient,
    private config: ConfigurationService,
    private utilService: UtilityService,
    private authSvc: AuthService,
    private resourceManagementService: ResourceManagementService,
    private stateManagementService: StateManagementService
  ) {
    let hisAdmissionUrl = config.getConfig().hisAdmissionUrl;
    if (
      this.config.getConfig().isLocal &&
      this.config.getConfig().localEndpoints.hisAdmissionUrl
    ) {
      hisAdmissionUrl = this.config.getConfig().localEndpoints.hisAdmissionUrl;
    }
    this.baseUrl = hisAdmissionUrl + "/his/";
    this.getOrgTimezone();
    this.getNameSignatureByExternalId();
  }
  getNameSignatureByExternalId() {
    this.resourceManagementService
      .getNameForSignatureByExternalId(this.authSvc.getUser().qbosUserId)
      .subscribe((name) => {
        this.validSignature = name.toString();
      });

    this.stateManagementService.newBillingFacility.subscribe((x) => {
      if (x !== null) {
        this.isBillingFacility = x;
      }
    });
  }

  setTabIcons() {
    this.tabList = JSON.parse(JSON.stringify(this.newTabList));
    this.tabList.forEach((tab) => {
      const sectionData = (this.admissionReportDetail || {})[tab.name];

      // A section the API omitted stays on the default icon. Reading
      // `sectionData.complete` unguarded here used to throw, and because
      // setTabIcons runs inside the rxjs tap of setAdmissionReportDetail /
      // prePopulateNewReport the throw reached the page's subscribe: showForms
      // was never set and the loading spinner was never dismissed.
      if (!sectionData) return;

      if (this.isSectionInProgress(sectionData))
        tab.icon = commonVar.IN_PROGRESS_ICON;

      if (sectionData.complete) tab.icon = commonVar.COMPLETED_ICON;
    });
  }

  isSectionInProgress(sectionData: any): boolean {
    let result = false;

    if (!sectionData) return result;

    const objKeys = Object.keys(sectionData).filter(
      (key) => key !== "complete" && key !== "isComplete"
    );

    objKeys.forEach((key) => {
      if (sectionData[key] instanceof Array && sectionData[key].length == 0) {
        return false;
      } else {
        if (sectionData[key] && sectionData[key] !== "") {
          result = true;
        }
      }
    });

    return result;
  }

  // List of Admission Reports for a given patient id
  getAdmissionReportList(page: number = 0) {
    const resourceUrl = `${this.baseUrl}report/${this.patientId}/admission/search?page=${page}&size=20`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.get(resourceUrl, { headers });
  }

  setAdmissionReportDetail(reportId) {
    const resourceUrl = `${this.baseUrl}report/admission/${reportId}`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http
      .get(resourceUrl, { headers })
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((reportDetail) => {
          this.admissionReportDetail = reportDetail;
          this.setTabIcons();
        })
      );
  }

  deleteAdmissionReport(reportId) {
    const resourceUrl = `${this.baseUrl}report/admission/${reportId}`;
    return this.http.delete(resourceUrl);
  }

  prePopulateNewReport(
    benefitToggle: boolean,
    hisV2Toggle: boolean,
    billingFacilityToggle: boolean
  ) {
    return this.http
      .get(
        `${this.baseUrl}report/${this.patientId}/admission/init?benefitToggle=${benefitToggle}&hisV2Toggle=${hisV2Toggle}&billingFacilityToggle=${billingFacilityToggle}`
      )
      // The fetch path normalises the payload; the pre-populate path did not,
      // so a new report was the one case where a section could reach the forms
      // undefined. Both paths now go through the same normalisation.
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((reportDetail) => {
          this.admissionReportDetail = reportDetail;
          this.admissionReportDetail.initialValue = true;
          this.setTabIcons();
        })
      );
  }

  mapZipNumber(reportDetail) {
    const zipNumber = reportDetail.administrationInfo.A0550;
    if (zipNumber && zipNumber.length > 5) {
      reportDetail.administrationInfo.A0550A = zipNumber.substring(0, 5);
      reportDetail.administrationInfo.A0550B = zipNumber.substring(5);
    } else {
      reportDetail.administrationInfo.A0550A = zipNumber;
      reportDetail.administrationInfo.A0550B = "";
    }

    return reportDetail;
  }

  updateAdmissionReport(formSection, data) {
    const url = `${this.baseUrl}report/admission/${this.activeReportId}`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");

    const update = {
      reportId: this.activeReportId,
      patientId: this.patientId,
      type: "admission",
      timezone: this.orgTimezone,
      endDate: this.getDateWithOrg(),
    };

    update[formSection] = data;

    return this.http
      .put(url, update, { headers: headers })
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((reportDetail) => {
          this.admissionReportDetail = reportDetail;
          this.admissionReportDetailChange.next(reportDetail);
          this.setTabIcons();
        })
      );
  }

  createAdmissionReport(newReport) {
    const resourceUrl = `${this.baseUrl}report/${this.patientId}/admission`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    const payload = {
      ...newReport,
      type: "admission",
      patientId: this.patientId,
      timezone: this.orgTimezone,
      startDate: this.getDateWithOrg(),
    };

    return this.http
      .post(resourceUrl, payload, { headers })
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((newReport: any) => {
          this.admissionReportDetail = newReport;
          this.activeReportId = newReport.id;
          this.admissionReportDetailChange.next(newReport);
          this.setTabIcons();
        })
      );
  }

  isValidSignature(): boolean {
    if (
      this.admissionReportDetail.administrationInfo.A0050 === "1" &&
      this.getARListCount() > 1 &&
      this.checkSectionsCompletion() &&
      !this.admissionReportDetail.endDate
    ) {
      return (
        this.admissionReportDetail.admissionRecordAdministration.Z0500A ===
        this.validSignature
      );
    } else {
      return true;
    }
  }

  getARListCount(): number {
    // admissionReportList is populated by the two list screens (report-grid and
    // hqrp-report-list). A deep link straight to detail-admission/:reportId —
    // a bookmark, a refresh, a link out of an email — never runs either, so
    // treat an unloaded list as "no prior exported record" rather than throwing.
    const reportList = this.admissionReportList || [];
    for (let i = 0; i < reportList.length; i++) {
      if (reportList[i].reportStatus === "EXPORTED") {
        return 2;
      }
    }
    return 0;
  }

  initializeNullValues(reportDetail) {
    var newReportDetail = reportDetail;

    if (!newReportDetail.administrationInfo)
      newReportDetail.administrationInfo = {};
    // activeDiagnosis was missing from this list even though the I - Active
    // Diagnosis tab, checkSectionsCompletion() and setTabIcons() all index
    // straight into it.
    if (!newReportDetail.activeDiagnosis) newReportDetail.activeDiagnosis = {};
    if (!newReportDetail.healthCondition) newReportDetail.healthCondition = {};
    if (!newReportDetail.medication) newReportDetail.medication = {};
    if (!newReportDetail.preference)
      newReportDetail.preference = { F2000A: "" };
    if (!newReportDetail.admissionRecordAdministration)
      newReportDetail.admissionRecordAdministration = {};

    return newReportDetail;
  }

  formatDate(date) {
    return date ? moment(date).format("MM-DD-YYYY") : null;
  }

  formatDateYYYYMMDD(date) {
    return date ? moment(date).format("YYYYMMDD") : null;
  }

  formatUTCDatetoLocal(date) {
    if (date) {
      date = moment.utc(date).local().format();
    }

    return date ? date : null;
  }

  formatlocalDatetoUTC(date) {
    if (date) {
      date = moment.parseZone(date).utc().format("YYYYMMDDTHHmmssZZ");
    }
    return date ? date : null;
  }

  checkSectionsCompletion() {
    const detail = this.admissionReportDetail || {};
    // A section the API omitted is by definition not complete — which is what
    // the caller wants to know. Reading `.complete` off it used to throw.
    return [
      "administrationInfo",
      "preference",
      "activeDiagnosis",
      "healthCondition",
      "medication",
    ].every((section) => !!(detail[section] && detail[section].complete));
  }

  setTenantId(tenantId) {
    this.tenantId = tenantId;
  }

  getTenantId() {
    return this.tenantId;
  }

  getPatientId() {
    return this.patientId;
  }

  getPatientInformation() {
    if (this.patientId) {
      const resourceUrl = `${this.baseUrl}patient/${this.patientId}?billingFacilityToggle=${this.isBillingFacility}`;
      const headers = new HttpHeaders().set("Content-Type", "application/json");
      return this.http.get(resourceUrl, { headers });
    }
  }

  getDateWithOrg(): string {
    let date = this.utilService.convertDateTimeWithTimezone(
      new Date(),
      this.orgTimezone
    );
    return date;
  }

  getOrgTimezone() {
    this.orgTimezone = this.authSvc.getUser().timeZoneOrg;
    // In case a valid time zone is not returned from RBAC, let's use the user/browser time zone
    if (!this.orgTimezone) {
      this.orgTimezone = momentTZ.tz.guess();
    }
  }

  getLegacyHISReportList() {
    if (this.patientId) {
      const resourceUrl = `${this.baseUrl}patient/legacy/${this.patientId}`;
      const headers = new HttpHeaders().set("Content-Type", "application/json");
      return this.http.get(resourceUrl, { headers });
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/admission-his/admission-his.ts